import { SourceMapConsumer } from 'source-map';

const WASM_URL = 'https://unpkg.com/source-map@0.7.4/lib/mappings.wasm';

const sourceMapCache = new Map<string, SourceMapConsumer>();
const loadingPromises = new Map<string, Promise<SourceMapConsumer | null>>();
let initialized = false;

type ParsedStackLine = {
  functionName: string | null;
  url: string;
  line: number;
  column: number;
};

type TranslatedLine = { line: string; isIgnored: boolean };

function initialize(): void {
  if (initialized) return;
  if (typeof (SourceMapConsumer as any)?.initialize !== 'function') return;

  try {
    (SourceMapConsumer as any).initialize({ 'lib/mappings.wasm': WASM_URL });
    initialized = true;
  } catch {
    // Silently fail - source map translation is best-effort
  }
}

export function clearSourceMapCache(): void {
  for (const consumer of sourceMapCache.values()) {
    const destroyable = consumer as { destroy?: () => void };
    if (typeof destroyable.destroy === 'function') {
      destroyable.destroy();
    }
  }
  sourceMapCache.clear();
  loadingPromises.clear();
}

const IDEAL_STACK_LINES = 10;

export async function translateStackTrace(stackTrace: string): Promise<string> {
  if (!stackTrace) return stackTrace;

  try {
    initialize();
    if (!initialized) return stackTrace;

    const lines = stackTrace.split('\n');
    const translatedInfo = await Promise.all(lines.map(translateLine));
    const condensedInfo = condenseStackLines(translatedInfo);
    return condensedInfo.map((info) => info.line).join('\n');
  } catch {
    return stackTrace;
  }
}

function condenseStackLines(lineInfo: TranslatedLine[]): TranslatedLine[] {
  if (lineInfo.length <= IDEAL_STACK_LINES) {
    return lineInfo;
  }

  const result = [...lineInfo];

  for (let i = result.length - 1; i >= 0 && result.length > IDEAL_STACK_LINES; i--) {
    if (result[i].isIgnored) {
      result.splice(i, 1);
    }
  }

  return result;
}

function parseStackLine(line: string): ParsedStackLine | null {
  // Chrome: "    at functionName (url:line:col)" or "    at url:line:col"
  const chromeMatch = line.match(/^\s*at\s+(?:async\s+)?(?:(.+?)\s+\()?(.+):(\d+):(\d+)\)?$/);
  if (chromeMatch) {
    return {
      functionName: chromeMatch[1] || null,
      url: chromeMatch[2],
      line: parseInt(chromeMatch[3], 10),
      column: parseInt(chromeMatch[4], 10),
    };
  }

  // Firefox: "functionName@url:line:col" or "@url:line:col"
  const firefoxMatch = line.match(/^(.*)@(.+):(\d+):(\d+)$/);
  if (firefoxMatch) {
    return {
      functionName: firefoxMatch[1] || null,
      url: firefoxMatch[2],
      line: parseInt(firefoxMatch[3], 10),
      column: parseInt(firefoxMatch[4], 10),
    };
  }

  return null;
}

function isJavaScriptUrl(url: string): boolean {
  return /\.(js|mjs|jsx|ts|tsx)(\?|$)/i.test(url);
}

function isVendorUrl(url: string): boolean {
  return url.includes('node_modules') || url.includes('vendors-');
}

function isEditableSource(sourcePath: string): boolean {
  return sourcePath.includes('src/generated/');
}

function extractVendorPackage(url: string): string {
  // "vendors-node_modules_@chakra-ui_react_..." → "@chakra-ui/react"
  // "node_modules/@chakra-ui/react/..." → "@chakra-ui/react"
  const match = url.match(/node_modules[_/](@[^_/]+[_/][^_/]+|[^_/]+)/);
  if (match) {
    return match[1].replace(/_/g, '/');
  }
  return '<vendor>';
}

async function translateLine(line: string): Promise<TranslatedLine> {
  try {
    const parsed = parseStackLine(line);
    if (!parsed || !isJavaScriptUrl(parsed.url)) {
      return { line, isIgnored: false };
    }

    const original = await getOriginalPosition(parsed.url, parsed.line, parsed.column);

    if (original?.source) {
      const functionName = original.name || parsed.functionName;
      const isVendor = isVendorUrl(original.source);
      const sourcePath = isVendor ? extractVendorPackage(original.source) : original.source;
      const suffix = !isVendor && !isEditableSource(original.source) ? ' (read-only)' : '';
      return {
        line: formatLine(functionName, sourcePath, original.line ?? 0, original.column ?? 0) + suffix,
        isIgnored: isVendor,
      };
    }

    return {
      line: formatLine(parsed.functionName, extractFilename(parsed.url), parsed.line, parsed.column),
      isIgnored: false,
    };
  } catch {
    return { line, isIgnored: false };
  }
}

function formatLine(functionName: string | null, source: string, line: number, column: number): string {
  if (functionName) {
    return `    at ${functionName} (${source}:${line}:${column})`;
  }
  return `    at ${source}:${line}:${column}`;
}

function extractFilename(filePath: string): string {
  try {
    const pathname = new URL(filePath).pathname;
    return pathname.split('/').pop() || filePath;
  } catch {
    return filePath.split('/').pop() || filePath;
  }
}

type OriginalPosition = ReturnType<SourceMapConsumer['originalPositionFor']>;

async function getOriginalPosition(url: string, line: number, column: number): Promise<OriginalPosition | null> {
  if (!initialized) return null;

  try {
    const resolvedUrl = resolveToFullUrl(url);
    const consumer = await getSourceMapConsumer(resolvedUrl);
    if (!consumer) return null;

    const position = consumer.originalPositionFor({ line, column });
    return position.source ? position : null;
  } catch {
    return null;
  }
}

function resolveToFullUrl(url: string): string {
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  // For relative paths/filenames, search loaded scripts
  const scripts = Array.from(document.scripts);
  const match = scripts.find((s) => s.src && s.src.endsWith(url));
  if (match) {
    return match.src;
  }

  // Fallback: assume it's relative to current origin
  if (url.startsWith('/')) {
    return window.location.origin + url;
  }

  return window.location.origin + '/static/js/' + url;
}

async function getSourceMapConsumer(url: string): Promise<SourceMapConsumer | null> {
  const cached = sourceMapCache.get(url);
  if (cached) return cached;

  const pending = loadingPromises.get(url);
  if (pending) return pending;

  const loadPromise = loadSourceMapConsumer(url);
  loadingPromises.set(url, loadPromise);

  try {
    const consumer = await loadPromise;
    if (consumer) {
      sourceMapCache.set(url, consumer);
    }
    return consumer;
  } catch {
    return null;
  } finally {
    loadingPromises.delete(url);
  }
}

async function loadSourceMapConsumer(url: string): Promise<SourceMapConsumer | null> {
  try {
    const sourceMapUrl = resolveSourceMapUrl(url);

    const response = await fetch(sourceMapUrl);
    if (!response.ok) return null;

    const text = await response.text();
    if (!text.trim().startsWith('{')) return null;

    return new SourceMapConsumer(text as any) as unknown as SourceMapConsumer;
  } catch {
    return null;
  }
}

function resolveSourceMapUrl(jsUrl: string): string {
  return `${jsUrl}.map`;
}
